import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, XAxis, YAxis, } from 'recharts';
import styled from 'styled-components';
import { Flex } from '@magnetic/flex';
import { Skeleton } from '@magnetic/skeleton';
import { floorStatusByPercentage } from '../../../../../common/util';
import { FLOOR_STATUS_COLOR } from '../../../../../constants';
import { formatLocalizedHour } from '../../../../../utils/locale-format';
import CircleWithLabel from '../../../common/circleWithLabel/index';
import moment from 'moment';
import 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { getBuildingAnalytics, getLocation, getContext, } from '../../../redux/spaces-slice';
import { hasValue } from '../../../navigator/util';
const OCCUPANCY_SOFT_TIMEOUT_MS = 30000;
export default function Occupancy({ isFullWidth = true, isSingleFloor }) {
    const { t, i18n } = useTranslation();
    const buildingAnalytics = useSelector(getBuildingAnalytics);
    const context = useSelector(getContext);
    const buildingConfig = context.appConfig.building;
    const [anyTelemetryWidgetsEnabled, setAnyTelemetryWidgetsEnabled] = useState(false);
    const location = useSelector(getLocation);
    const [previousToday, setPreviousToday] = useState({});
    const [occupancyLevel, setOccupancyLevel] = useState('');
    const [totalValue, setTotalValue] = useState(0);
    const [hasUsableOccupancyData, setHasUsableOccupancyData] = useState(false);
    const [hasSoftTimedOut, setHasSoftTimedOut] = useState(false);
    const [dataToUse, setDataToUse] = useState([]);
    useEffect(() => {
        const { weekly, hourly, capacity } = buildingAnalytics;
        const hasValidOccupancyData = Array.isArray(weekly) &&
            Array.isArray(hourly) &&
            Number(capacity) > 0 &&
            weekly.length > 0 &&
            hourly.length > 0 &&
            Boolean(context.timeZoneId);
        if (!hasValidOccupancyData) {
            setHasUsableOccupancyData(false);
            setPreviousToday({});
            setOccupancyLevel('');
            setTotalValue(0);
            setDataToUse([]);
        }
        if (hasValidOccupancyData) {
            setPreviousToday(hourly[hourly.length - 1]);
            const data = weekly
                .map((h, i) => {
                const perHour = {
                    name: `${i}:00`,
                    weekly: Math.ceil((h / capacity) * 100),
                    today: 1,
                };
                if (hourly.length > i && hourly[i] >= 0) {
                    perHour.today = Math.ceil((hourly[i] / capacity) * 100);
                    setPreviousToday(perHour);
                }
                return perHour;
            })
                .slice(6, 21);
            setTotalValue(data.reduce((accumulator, object) => accumulator + (object.today || 0) + (object.weekly || 0), 0));
            const buildingTimeZone = context.timeZoneId;
            const currentHour = moment().tz(buildingTimeZone).clone().hour();
            setDataToUse(currentHour < 12 ? data.slice(0, 12) : data.slice(-12));
            setOccupancyLevel(floorStatusByPercentage(Math.ceil((buildingAnalytics.peopleCount / capacity) * 100)));
            setHasUsableOccupancyData(true);
            setHasSoftTimedOut(false);
        }
        setAnyTelemetryWidgetsEnabled((anyTelemetryWidgetsEnabled) => anyTelemetryWidgetsEnabled || (buildingAnalytics === null || buildingAnalytics === void 0 ? void 0 : buildingAnalytics.ambientNoise));
    }, [buildingAnalytics]);
    useEffect(() => {
        if (hasUsableOccupancyData) {
            return;
        }
        const timeoutId = window.setTimeout(() => {
            setHasSoftTimedOut(true);
        }, OCCUPANCY_SOFT_TIMEOUT_MS);
        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [hasUsableOccupancyData]);
    useEffect(() => {
        setAnyTelemetryWidgetsEnabled((buildingConfig === null || buildingConfig === void 0 ? void 0 : buildingConfig.showAirQuality) ||
            (buildingConfig === null || buildingConfig === void 0 ? void 0 : buildingConfig.showCO2) ||
            (buildingConfig === null || buildingConfig === void 0 ? void 0 : buildingConfig.showTemperature) ||
            (buildingConfig === null || buildingConfig === void 0 ? void 0 : buildingConfig.showHumidity) ||
            hasValue(buildingAnalytics === null || buildingAnalytics === void 0 ? void 0 : buildingAnalytics.ambientNoise));
    }, []);
    const showLoadingState = !hasUsableOccupancyData && !hasSoftTimedOut;
    const showNoDataState = !hasUsableOccupancyData && hasSoftTimedOut;
    // Placeholder x-axis labels (hours 6:00-20:00, windowed to 12 like the real
    // chart) so the empty graph still renders sensible axis ticks with no bars.
    const emptyChartData = React.useMemo(() => {
        const buildingTimeZone = context.timeZoneId;
        const currentHour = buildingTimeZone
            ? moment().tz(buildingTimeZone).clone().hour()
            : moment().hour();
        const base = Array.from({ length: 15 }, (_, idx) => ({
            name: `${idx + 6}:00`,
            weekly: 0,
            today: 0,
        }));
        return currentHour < 12 ? base.slice(0, 12) : base.slice(-12);
    }, [context.timeZoneId]);
    return showLoadingState ? (_jsx(StyledOccupancy, Object.assign({ isSingleFloor: isSingleFloor }, { children: _jsxs(Flex, Object.assign({ direction: "vertical", gap: 24, id: "building-occupancy" }, { children: [_jsx(Skeleton, { shade: "dark", style: {
                        width: '25%',
                    } }), _jsxs(Flex, Object.assign({ gap: 36 }, { children: [_jsx(Skeleton, { height: 300, style: {
                                width: '70%',
                            } }), _jsxs(Flex, Object.assign({ direction: "vertical", gap: 12, style: {
                                width: '30%',
                            } }, { children: [_jsx(Skeleton, { height: "sm" }), _jsx(Skeleton, { height: "sm" }), _jsx(Skeleton, { height: "sm" }), _jsx(Skeleton, { height: "sm" }), _jsx(Skeleton, { height: "sm" }), _jsx(Skeleton, { height: "sm" })] }))] }))] })) }))) : showNoDataState ? (_jsxs(StyledOccupancy, Object.assign({ isSingleFloor: isSingleFloor }, { children: [_jsxs(Title, { children: [t('current-occupancy'), ":"] }), _jsx(NoDataStatus, { children: t('floor.counts-widget.no-data') }), _jsx(ChartContainer, Object.assign({ style: {
                    height: isFullWidth
                        ? !isSingleFloor
                            ? '50.407vh'
                            : '30.407vh'
                        : !isSingleFloor
                            ? '50.407vh'
                            : '22.407vh',
                    width: !anyTelemetryWidgetsEnabled ? '60vw' : '',
                } }, { children: _jsx(ResponsiveContainer, Object.assign({ width: "100%", height: '100%' }, { children: _jsxs(BarChart, Object.assign({ data: emptyChartData, height: 1000 }, { children: [_jsx(CartesianGrid, { vertical: false, stroke: "#333335", strokeWidth: "0.5px", strokeDasharray: "1" }), _jsx(XAxis, { dataKey: "name", xAxisId: 0, interval: 0, tickFormatter: (value) => formatLocalizedHour(value, i18n.resolvedLanguage || i18n.language, (location === null || location === void 0 ? void 0 : location.use24HrClockStyle) === true), padding: { left: 10, right: 10 }, axisLine: { stroke: '#333335' }, tick: { fill: '#A9A9A9', fontWeight: '700', fontSize: '.75vw' } }), _jsx(YAxis, { domain: [0, 100], axisLine: false, tickLine: false, tickCount: 6, tickFormatter: (value) => `${value}%`, tick: { fill: '#A9A9A9', fontWeight: '700', fontSize: '.75vw' } })] })) })) }))] }))) : (_jsxs(StyledOccupancy, Object.assign({ isSingleFloor: isSingleFloor }, { children: [_jsxs(Title, { children: [t('current-occupancy'), ":"] }), _jsx(Status, Object.assign({ occupancyLevel: occupancyLevel }, { children: t(`current-occupancy.${occupancyLevel}`) })), _jsx(ChartContainer, Object.assign({ style: {
                    height: isFullWidth
                        ? !isSingleFloor
                            ? '50.407vh'
                            : '30.407vh'
                        : !isSingleFloor
                            ? '50.407vh'
                            : '22.407vh',
                    width: !anyTelemetryWidgetsEnabled ? '60vw' : '',
                } }, { children: _jsx(ResponsiveContainer, Object.assign({ width: "100%", height: '100%' }, { children: _jsxs(BarChart, Object.assign({ data: dataToUse, height: 1000 }, { children: [_jsx(CartesianGrid, { vertical: false, stroke: "#333335", strokeWidth: "0.5px", strokeDasharray: "1" }), _jsxs("defs", { children: [_jsxs("linearGradient", Object.assign({ id: "colorUv", x1: "0", y1: "0", x2: "0", y2: "100%", gradientUnits: "userSpaceOnUse" }, { children: [_jsx("stop", { offset: "0.2", stopColor: "#0c56ae" }), _jsx("stop", { offset: "0.5", stopColor: "#0c56ae" }), _jsx("stop", { offset: "1", stopColor: "#122D4E" })] })), _jsxs("linearGradient", Object.assign({ id: "colorUvHigh", x1: "0", y1: "0", x2: "0", y2: "100%", gradientUnits: "userSpaceOnUse" }, { children: [_jsx("stop", { offset: "0.2", stopColor: "#00BCEB" }), _jsx("stop", { offset: "0.5", stopColor: "#00BCEB" }), _jsx("stop", { offset: "1", stopColor: "#0075FF" })] }))] }), _jsx(XAxis, { dataKey: "name", xAxisId: 0, interval: 0, tickFormatter: (value) => formatLocalizedHour(value, i18n.resolvedLanguage || i18n.language, (location === null || location === void 0 ? void 0 : location.use24HrClockStyle) === true), padding: { left: 10, right: 10 }, axisLine: { stroke: '#333335' }, tick: { fill: '#A9A9A9', fontWeight: '700', fontSize: '.75vw' } }), _jsx(XAxis, { dataKey: "name", xAxisId: 1, interval: 0, hide: true, padding: { left: 10, right: 10 } }), _jsx(YAxis, { domain: [0, 100], axisLine: false, tickLine: false, tickCount: 6, tickFormatter: (value) => `${value}%`, tick: { fill: '#A9A9A9', fontWeight: '700', fontSize: '.75vw' } }), _jsx(Bar, { dataKey: "weekly", barSize: isFullWidth ? 35 : 26, stackId: "a", xAxisId: 1, fill: "#2D3134", stroke: "#464a4d", strokeWidth: 1, radius: [8, 8, 0, 0] }), _jsx(Bar, Object.assign({ legendType: "circle", dataKey: "today", barSize: isFullWidth ? 50 : 27.25, stackId: "b", xAxisId: 0, fill: "url(#colorUv)", fillOpacity: 1, radius: [8, 8, 0, 0] }, { children: dataToUse.map((entry, index, entries) => (_jsx(Cell, { cursor: "pointer", stroke: entry.today > 1 ? '#204c93' : 'none', strokeWidth: entry.today > 1 ? 1 : 0, fill: entry.today > 1
                                        ? entryMatched(entry, previousToday)
                                            ? 'url(#colorUvHigh)'
                                            : 'url(#colorUv)'
                                        : 'none', id: `cell-${index}` }, `cell-${index}`))) }))] })) })) })), _jsxs(ColorLegend, { children: [_jsx(CircleWithLabel, { borderColor: "#66E0FF", gradientStart: "#00BCEB", gradientEnd: "#0075FF", label: t('current-occupancy.right-now') }), _jsx(CircleWithLabel, { borderColor: "#2961A4", gradientStart: "#0C56AE", gradientEnd: "#122D4E", label: t('current-occupancy.earlier-today') }), _jsx(CircleWithLabel, { borderColor: "#9E9EA2", gradientStart: "#2D3134", gradientEnd: "#2D3134", label: t('current-occupancy.weekly-avg') })] })] })));
}
const StyledOccupancy = styled.div `
  flex: 1;
  min-height: fit-content;
  width: 40vw;
  .recharts-surface {
    height: ${({ isSingleFloor }) => !isSingleFloor ? '56.407vh' : '32.407vh '} !important;
  }
`;
const Title = styled.div `
  font-family: 'Sora';
  font-weight: 400;
  font-size: 1.5vw;
  line-height: 3vh;
  color: #ffffff;
`;
const Status = styled.div `
  padding-top: 0.5vw;
  padding-bottom: 1.5vw;
  font-family: 'Sora';
  font-weight: 900;
  font-size: 2.05vw;
  line-height: 1.75vw;
  color: ${(props) => { var _a; return FLOOR_STATUS_COLOR[(_a = props.occupancyLevel) === null || _a === void 0 ? void 0 : _a.toUpperCase()]; }};
`;
const NoDataStatus = styled.div `
  padding-top: 0.5vw;
  padding-bottom: 1.5vw;
  font-family: 'Sora';
  font-weight: 900;
  font-size: 2.05vw;
  line-height: 1.75vw;
  color: #9e9ea2;
`;
const ChartContainer = styled.div `
  padding-right: 2vw;

  @media (orientation: portrait) {
    padding-right: 1vw;
  }
`;
const ColorLegend = styled.div `
  margin-top: 1.042vw;
  display: flex;
  align-items: center;
  justify-content: start;

  @media (orientation: portrait) {
    margin-top: 2.7rem;
    gap: 0.45rem 0.8rem;
    flex-wrap: wrap;
    align-items: flex-start;
  }
`;
const entryMatched = (entry, previousToday) => {
    if ((entry === null || entry === void 0 ? void 0 : entry.name) === previousToday.name)
        return true;
    return false;
};
Occupancy.propTypes = {
    isFullWidth: PropTypes.bool,
};
Occupancy.defaultProps = {
    isFullWidth: PropTypes.bool,
};
